import React, { useEffect, useState } from "react";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button, notification, message, Tooltip, Space } from "antd";
import { FileExcelOutlined, FileTextOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import getComparativoAnalisisMedicion from "../../../services/visitas_tecnicas/getComparativoAnalisisMedicionService";
import normalizarCoordenada from "../../../utils/normalizarGeoreferencias";

const Export_ComparativoAnalisisMedicion = () => {
  const [dataCompleta, setDataCompleta] = useState([]);
  const [loading, setLoading] = useState(false); // Estado de carga
  const [loadingExcel, setLoadingExcel] = useState(false); // Estado para Excel
  const [loadingJson, setLoadingJson] = useState(false); // Estado para JSON
  const usernameFromState = useSelector((state) => state.auth.user);

  const fetchDetalleVisitas = async () => {
    try {
      const response = await getComparativoAnalisisMedicion();
      const { status, data } = response;

      if (status) {
        setDataCompleta(data || []);
      } else {
        notification.error({
          message: "Error",
          description: message || "Failed to fetch detalle visitas data.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    } finally {
    }
  };

  useEffect(() => {
    fetchDetalleVisitas();
  }, []);

  function groupByLineaProductiva(datos) {
    const resultado = {};

    for (const item of datos) {
      const {
        id_participante,
        estado,
        detalle_tipo_3,
        detalle_tipo_8,
        cambio_linea_prod,
        detalle_ultima_novedad,
        departamento,
        municipio,
      } = item;

      const linea3 = detalle_tipo_3?.lineaProductiva?.trim();
      const linea8 = detalle_tipo_8?.lineaProductiva?.trim();

      // 🔹 Caso: ambos existen y tienen la misma línea productiva
      if (linea3 && linea8 && linea3 === linea8) {
        if (!resultado[linea3]) resultado[linea3] = [];
        resultado[linea3].push({
          estado,
          id_participante,
          detalle_tipo_3,
          detalle_tipo_8,
          cambio_linea_prod,
          detalle_ultima_novedad,
          departamento,
          municipio,
        });
        continue; // Evitamos procesarlos por separado
      }

      // 🔹 Caso: solo detalle_tipo_3
      if (linea3) {
        if (!resultado[linea3]) resultado[linea3] = [];
        resultado[linea3].push({
          estado,
          id_participante,
          detalle_tipo_3,
          cambio_linea_prod,
          detalle_ultima_novedad,
          departamento,
          municipio,
        });
      }

      // 🔹 Caso: solo detalle_tipo_8
      if (linea8) {
        if (!resultado[linea8]) resultado[linea8] = [];
        resultado[linea8].push({
          estado,
          id_participante,
          detalle_tipo_8,
          cambio_linea_prod,
          detalle_ultima_novedad,
          departamento,
          municipio,
        });
      }

      // 🔹 Caso: ninguno tiene línea productiva
      if (!linea3 && !linea8) {
        if (!resultado["Sin línea productiva"])
          resultado["Sin línea productiva"] = [];
        resultado["Sin línea productiva"].push({
          id_participante,
        });
      }
    }

    return resultado;
  }

  // 📊 Asigna valores a la hoja
  const asignarCalificaciones = (worksheet, row, obj, columnas, keys) => {
    if (!obj || !Array.isArray(keys)) return [];
    return keys.map((key, index) => {
      const valor = obj?.[key]?.valor || 0;
      worksheet.getCell(`${columnas[index]}${row}`).value = valor;
      return valor;
    });
  };

  function procesarBloque({
    worksheet,
    row,
    detalle3,
    detalle8,
    colsM1,
    colsM2,
    keys,
    path,
    columnasSalida = [],
  }) {
    const totalCalificacion = keys.length * 5;
    let sumaM1 = 0,
      sumaM2 = 0,
      porcentajeM1 = 0,
      porcentajeM2 = 0;

    // --- M1 (detalle tipo 3)
    if (detalle3) {
      const data3 = detalle3?.[path]?.calificacion || {};
      const valoresM1 = asignarCalificaciones(
        worksheet,
        row,
        data3,
        colsM1,
        keys
      );

      sumaM1 = Array.isArray(valoresM1)
        ? valoresM1.reduce((a, b) => a + (Number(b) || 0), 0)
        : colsM1.reduce(
            (total, col) =>
              total + (Number(worksheet.getCell(`${col}${row}`).value) || 0),
            0
          );

      porcentajeM1 = (sumaM1 / totalCalificacion) * 100;
    } else {
      colsM1.forEach((col) => {
        worksheet.getCell(`${col}${row}`).value = "No aplica";
      });
    }

    // --- M2 (detalle tipo 8)
    if (detalle8) {
      const data8 = detalle8?.[path]?.calificacion || {};
      const valoresM2 = asignarCalificaciones(
        worksheet,
        row,
        data8,
        colsM2,
        keys
      );

      sumaM2 = Array.isArray(valoresM2)
        ? valoresM2.reduce((a, b) => a + (Number(b) || 0), 0)
        : colsM2.reduce(
            (total, col) =>
              total + (Number(worksheet.getCell(`${col}${row}`).value) || 0),
            0
          );

      porcentajeM2 = (sumaM2 / totalCalificacion) * 100;
    } else {
      colsM2.forEach((col) => {
        worksheet.getCell(`${col}${row}`).value = "No aplica";
      });
    }

    // --- Calcular diferencia solo si aplican ambos
    const tieneAmbos = detalle3 && detalle8;

    const diferencia = tieneAmbos ? sumaM2 - sumaM1 : "No aplica";
    const diferenciaPorcentual = tieneAmbos
      ? porcentajeM2 - porcentajeM1
      : "No aplica";

    // --- Escribir resultados
    if (columnasSalida.length >= 6) {
      worksheet.getCell(`${columnasSalida[0]}${row}`).value = totalCalificacion;
      worksheet.getCell(`${columnasSalida[1]}${row}`).value = detalle3
        ? sumaM1
        : "No aplica";
      worksheet.getCell(`${columnasSalida[2]}${row}`).value = detalle3
        ? `${porcentajeM1.toFixed(2)}%`
        : "No aplica";

      worksheet.getCell(`${columnasSalida[3]}${row}`).value = detalle8
        ? sumaM2
        : "No aplica";
      worksheet.getCell(`${columnasSalida[4]}${row}`).value = detalle8
        ? `${porcentajeM2.toFixed(2)}%`
        : "No aplica";

      worksheet.getCell(`${columnasSalida[5]}${row}`).value = diferencia;
      worksheet.getCell(`${columnasSalida[6]}${row}`).value =
        diferenciaPorcentual === "No aplica"
          ? "No aplica"
          : `${diferenciaPorcentual.toFixed(2)}%`;
    }

    return {
      sumaM1,
      sumaM2,
      porcentajeM1,
      porcentajeM2,
      diferencia,
      diferenciaPorcentual,
      totalCalificacion,
    };
  }

  const exportToExcel = async () => {
    try {
      if (!dataCompleta || dataCompleta.length === 0) {
        notification.warning({
          message: "Advertencia",
          description: "No hay datos disponibles para exportar.",
          duration: 3,
        });
        return;
      }

      function safeNumber(v) {
        return typeof v === "number" ? v : 0;
      }
      setLoadingExcel(true);
      const workbook = new ExcelJS.Workbook();

      // Cargar la plantilla de Excel
      const templateResponse = await fetch(
        "/assets/plantilla_comparativo.xlsx"
      );
      const templateBuffer = await templateResponse.arrayBuffer();

      await workbook.xlsx.load(templateBuffer);

      function llenarColumnasComunes(worksheet, item, currentRow, estado) {
        // Paso 1
        worksheet.getCell(`A${currentRow}`).value = item.id_participante || "";
        worksheet.getCell(`B${currentRow}`).value = item.modalidad_visita || "";
        worksheet.getCell(`C${currentRow}`).value = item.lugar_visita || "";
        worksheet.getCell(`D${currentRow}`).value =
          normalizarCoordenada(item.latuser) ?? "";
        worksheet.getCell(`E${currentRow}`).value =
          normalizarCoordenada(item.lnguser) ?? "";

        // Paso 2
        worksheet.getCell(`F${currentRow}`).value = estado || "";
        worksheet.getCell(`G${currentRow}`).value = item.quien_responde || "";
        worksheet.getCell(`H${currentRow}`).value = item.nombre_persona || "";
        worksheet.getCell(`I${currentRow}`).value = item.rango_edad || "";
        worksheet.getCell(`J${currentRow}`).value = item.genero || "";
        worksheet.getCell(`K${currentRow}`).value = item.estado_embarazo || "";

        worksheet.getCell(`L${currentRow}`).value =
          item.nivel_escolaridad || "";

        worksheet.getCell(`M${currentRow}`).value = item.grupo_etnico || "";

        worksheet.getCell(`N${currentRow}`).value = item.discapacidad || "";

        worksheet.getCell(`O${currentRow}`).value =
          item.tipo_discapacidad || "";

        worksheet.getCell(`P${currentRow}`).value =
          item.horas_actividad_cuidado || "";

        // Paso 3
        worksheet.getCell(`Q${currentRow}`).value = item.nombreProyecto || "";
        worksheet.getCell(`R${currentRow}`).value = item.codigoPreIni || "";
        worksheet.getCell(`S${currentRow}`).value = item.tipoProyecto || "";
        worksheet.getCell(`T${currentRow}`).value = item.estadoProyecto || "";
        worksheet.getCell(`U${currentRow}`).value = item.lineaProductiva || "";
        worksheet.getCell(`V${currentRow}`).value =
          item.cambioLineaProductiva || "";
        worksheet.getCell(`W${currentRow}`).value =
          item.documento_avala_cambio_diligenciado || "";
        worksheet.getCell(`X${currentRow}`).value = item.tenenciaPredio || "";
        worksheet.getCell(`Y${currentRow}`).value =
          item.otraTenenciaPredio || "";
        worksheet.getCell(`Z${currentRow}`).value = item.areaPredio || "";
        worksheet.getCell(`AA${currentRow}`).value = item.zonaPredio || "";
      }

      function setDiferenciaPorcentual(
        worksheet,
        colDestino,
        row,
        iniciales,
        finales,
        aplicar
      ) {
        if (aplicar === "SI") {
          worksheet.getCell(`${colDestino}${row}`).value = "";
          return;
        }

        const sumaFinales = finales.map((col) => `${col}${row}`).join("+");
        const sumaIniciales = iniciales.map((col) => `${col}${row}`).join("+");

        const maximo = iniciales.length * 5;

        worksheet.getCell(`${colDestino}${row}`).value = {
          formula: `ROUND(((${sumaFinales}-(${sumaIniciales}))/${maximo}*100),2) & "%"`,
        };
      }

      const procesarHojaAgricola = async (worksheet, data) => {
        if (!Array.isArray(data) || data.length === 0) return;

        let currentRow = 5;

        for (const item of data) {
          const detalle = item.detalle_tipo_3 || item.detalle_tipo_8 || null;
          const estado = item.estado || "";
          const cambio_linea_prod = item?.cambio_linea_prod || "";

          if (detalle) {
            llenarColumnasComunes(worksheet, detalle, currentRow, estado);
          } else {
            console.warn(
              `⚠️ Sin detalle para participante ${item.id_participante}`
            );
          }

          const detalle3 = item.detalle_tipo_3 || null;
          const detalle8 = item.detalle_tipo_8 || null;

          // --- 🟢 Bloque SOCIOEMPRESARIAL
          const socio = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: [
              "AB",
              "AE",
              "AG",
              "AI",
              "AL",
              "AN",
              "AP",
              "AR",
              "AT",
              "AW",
            ],
            colsM2: [
              "AC",
              "AF",
              "AH",
              "AJ",
              "AM",
              "AO",
              "AQ",
              "AS",
              "AU",
              "AX",
            ],
            keys: [
              "1.1",
              "2.1",
              "2.2",
              "2.3",
              "3.1",
              "3.2",
              "3.3",
              "3.4",
              "3.5",
              "4.1",
            ],
            path: "capacidades_socio_empresarial",
            columnasSalida: ["AZ", "BA", "BB", "BC", "BD", "BE", "BF"], // Donde escribir resultados
          });

          setDiferenciaPorcentual(
            worksheet,
            "AD",
            currentRow,
            ["AB"], // iniciales
            ["AC"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -FINANCIERA
          setDiferenciaPorcentual(
            worksheet,
            "AK",
            currentRow,
            ["AE", "AG", "AI"], // iniciales
            ["AF", "AH", "AJ"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -COMERCIAL
          setDiferenciaPorcentual(
            worksheet,
            "AV",
            currentRow,
            ["AL", "AN", "AP", "AR", "AT"], // iniciales (5)
            ["AM", "AO", "AQ", "AS", "AU"], // finales (5)
            cambio_linea_prod
          );

          // Diferencia porcentual -COMERCIAL
          setDiferenciaPorcentual(
            worksheet,
            "AV",
            currentRow,
            ["AL", "AN", "AP", "AR", "AT"], // iniciales (5)
            ["AM", "AO", "AQ", "AS", "AU"], // finales (5)
            cambio_linea_prod
          );

          // Diferencia porcentual -TICS
          setDiferenciaPorcentual(
            worksheet,
            "AY",
            currentRow,
            ["AW"], // iniciales (5)
            ["AX"], // finales (5)
            cambio_linea_prod
          );

          // --- 🟢 Bloque ENFOQUE TRANSVERSAL
          const enfoque = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: ["BG", "BI"],
            colsM2: ["BH", "BJ"],
            keys: ["1.1", "1.2"],
            path: "capacidades_enfoque_transversal",
            columnasSalida: ["BK", "BL", "BM", "BN", "BO", "BP", "BQ"],
          });

          // --- 🟢 CAPACIDADES AGRICOLAS
          const capacidades_agricolas = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: [
              "BR",
              "BT",
              "BV",
              "BX",
              "BZ",
              "CC",
              "CE",
              "CH",
              "CK",
              "CM",
              "CO",
              "CR",
              "CT",
              "CV",
              "CY",
              "DA",
              "DD",
              "DF",
              "DH",
              "DJ",
            ],
            colsM2: [
              "BS",
              "BU",
              "BW",
              "BY",
              "CA",
              "CD",
              "CF",
              "CI",
              "CL",
              "CN",
              "CP",
              "CS",
              "CU",
              "CW",
              "CZ",
              "DB",
              "DE",
              "DG",
              "DI",
              "DK",
            ],
            keys: [
              "1.1",
              "1.2",
              "1.3",
              "1.4",
              "1.5",
              "2.1",
              "2.2",
              "3.1",
              "4.1",
              "4.2",
              "4.3",
              "5.1",
              "5.2",
              "5.3",
              "6.1",
              "6.2",
              "7.1",
              "7.2",
              "7.3",
              "7.4",
            ],
            path: "capacidades_agricola",
            columnasSalida: ["DM", "DN", "DO", "DP", "DQ", "DR", "DS"],
          });

          // Diferencia porcentual -PLANIFICACION PREDIAL/MANEJO DE LA UNIDAD PRODUCTIVA
          setDiferenciaPorcentual(
            worksheet,
            "CB",
            currentRow,
            ["BR", "BT", "BV", "BX", "BZ"], // iniciales
            ["BS", "BU", "BW", "BY", "CA"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - COSTOS DE PRODUCCION
          setDiferenciaPorcentual(
            worksheet,
            "CG",
            currentRow,
            ["CC", "CE"], // iniciales
            ["CD", "CF"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -INFRAESTRUCTURA PRODUCTIVA
          setDiferenciaPorcentual(
            worksheet,
            "CJ",
            currentRow,
            ["CH"], // iniciales
            ["CI"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - NUTRICIÓN (FERTILIZACIÓN)
          setDiferenciaPorcentual(
            worksheet,
            "CQ",
            currentRow,
            ["CK", "CM", "CO"], // iniciales
            ["CL", "CN", "CP"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - MANEJO INTEGRADO DE PLAGAS Y ENFERMEDADES (MIP)
          setDiferenciaPorcentual(
            worksheet,
            "CX",
            currentRow,
            ["CR", "CT", "CV"], // iniciales
            ["CS", "CU", "CW"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -  BUENAS PRACTICAS PARA SISTEMA PRODUCTIVO
          setDiferenciaPorcentual(
            worksheet,
            "DC",
            currentRow,
            ["CY", "DA"], // iniciales
            ["CZ", "DB"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -  PROTECCION DE ECOSISTEMAS, SALUD DEL SUELO Y MANEJO DE RESIDUOS Y CUIDADO DEL AGUA
          setDiferenciaPorcentual(
            worksheet,
            "DL",
            currentRow,
            ["DD", "DF", "DH", "DJ"], // iniciales
            ["DE", "DG", "DI", "DK"], // finales
            cambio_linea_prod
          );

          const bloques = [socio, enfoque, capacidades_agricolas];

          // total de criterios (siempre válido)
          const totalGeneral = bloques.reduce(
            (acc, b) => acc + safeNumber(b.totalCalificacion),
            0
          );

          const detalle_ultima_novedad = item?.detalle_ultima_novedad || {};

          const mensajeNovedad = `Se realiza cambio de línea productiva: de Agrícola a ${
            detalle_ultima_novedad.name_sector_economico_cambio ?? ""
          }.`;

          worksheet.getCell(`DT${currentRow}`).value =
            cambio_linea_prod === "SI" ? mensajeNovedad : "NO";

          // sumatorias
          const sumaM1General = bloques.reduce(
            (acc, b) => acc + safeNumber(b.sumaM1),
            0
          );
          const sumaM2General = bloques.reduce(
            (acc, b) => acc + safeNumber(b.sumaM2),
            0
          );

          // porcentaje solo si aplica
          const porcentajeM1General =
            totalGeneral > 0
              ? (sumaM1General / totalGeneral) * 100
              : "No aplica";

          const porcentajeM2General =
            totalGeneral > 0
              ? (sumaM2General / totalGeneral) * 100
              : "No aplica";

          // verificar si existe al menos un bloque válido para comparar
          const puedeComparar = bloques.some(
            (b) => b.diferencia !== "No aplica"
          );

          const diferenciaGeneral = puedeComparar
            ? sumaM2General - sumaM1General
            : "No aplica";

          const diferenciaPorcentualGeneral = puedeComparar
            ? porcentajeM2General - porcentajeM1General
            : "No aplica";

          worksheet.getCell(`DU${currentRow}`).value = totalGeneral;
          worksheet.getCell(`DV${currentRow}`).value = sumaM1General;
          worksheet.getCell(`DW${currentRow}`).value =
            porcentajeM1General === "No aplica"
              ? "No aplica"
              : `${porcentajeM1General.toFixed(2)}%`;

          worksheet.getCell(`DX${currentRow}`).value = sumaM2General;
          worksheet.getCell(`DY${currentRow}`).value =
            porcentajeM2General === "No aplica"
              ? "No aplica"
              : `${porcentajeM2General.toFixed(2)}%`;

          worksheet.getCell(`DZ${currentRow}`).value = diferenciaGeneral;
          worksheet.getCell(`EA${currentRow}`).value =
            diferenciaPorcentualGeneral === "No aplica"
              ? "No aplica"
              : `${diferenciaPorcentualGeneral.toFixed(2)}%`;

          worksheet.getCell(`EB${currentRow}`).value = item.departamento || "";
          worksheet.getCell(`EC${currentRow}`).value = item.municipio || "";

          currentRow++;
        }
      };

      const procesarHojaPecuaria = async (worksheet, data) => {
        if (!Array.isArray(data) || data.length === 0) return;

        let currentRow = 5;

        for (const item of data) {
          const detalle = item.detalle_tipo_3 || item.detalle_tipo_8 || null;
          const estado = item.estado || "";
          const cambio_linea_prod = item?.cambio_linea_prod || "";

          if (detalle) {
            llenarColumnasComunes(worksheet, detalle, currentRow, estado);
          } else {
            console.warn(
              `⚠️ Sin detalle para participante ${item.id_participante}`
            );
          }
          const detalle3 = item.detalle_tipo_3 || null;
          const detalle8 = item.detalle_tipo_8 || null;

          // --- 🟢 Bloque SOCIOEMPRESARIAL
          const socio = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: [
              "AB",
              "AE",
              "AG",
              "AI",
              "AL",
              "AN",
              "AP",
              "AR",
              "AT",
              "AW",
            ],
            colsM2: [
              "AC",
              "AF",
              "AH",
              "AJ",
              "AM",
              "AO",
              "AQ",
              "AS",
              "AU",
              "AX",
            ],
            keys: [
              "1.1",
              "2.1",
              "2.2",
              "2.3",
              "3.1",
              "3.2",
              "3.3",
              "3.4",
              "3.5",
              "4.1",
            ],
            path: "capacidades_socio_empresarial",
            columnasSalida: ["AZ", "BA", "BB", "BC", "BD", "BE", "BF"], // Donde escribir resultados
          });

          setDiferenciaPorcentual(
            worksheet,
            "AD",
            currentRow,
            ["AB"], // iniciales
            ["AC"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -FINANCIERA
          setDiferenciaPorcentual(
            worksheet,
            "AK",
            currentRow,
            ["AE", "AG", "AI"], // iniciales
            ["AF", "AH", "AJ"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -COMERCIAL
          setDiferenciaPorcentual(
            worksheet,
            "AV",
            currentRow,
            ["AL", "AN", "AP", "AR", "AT"], // iniciales (5)
            ["AM", "AO", "AQ", "AS", "AU"], // finales (5)
            cambio_linea_prod
          );

          // Diferencia porcentual -COMERCIAL
          setDiferenciaPorcentual(
            worksheet,
            "AV",
            currentRow,
            ["AL", "AN", "AP", "AR", "AT"], // iniciales (5)
            ["AM", "AO", "AQ", "AS", "AU"], // finales (5)
            cambio_linea_prod
          );

          // Diferencia porcentual -TICS
          setDiferenciaPorcentual(
            worksheet,
            "AY",
            currentRow,
            ["AW"], // iniciales (5)
            ["AX"], // finales (5)
            cambio_linea_prod
          );

          // --- 🟢 Bloque ENFOQUE TRANSVERSAL
          const enfoque = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: ["BG", "BI"],
            colsM2: ["BH", "BJ"],
            keys: ["1.1", "1.2"],
            path: "capacidades_enfoque_transversal",
            columnasSalida: ["BK", "BL", "BM", "BN", "BO", "BP", "BQ"],
          });

          // --- 🟢  CAPACIDADES PECUARIA
          const capacidades_pecuario = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: [
              "BR",
              "BT",
              "BW",
              "BZ",
              "CC",
              "CF",
              "CH",
              "CJ",
              "CL",
              "CO",
              "CR",
              "CT",
              "CW",
              "CZ",
              "DC",
              "DE",
              "DG",
              "DI",
              "DK",
            ],
            colsM2: [
              "BS",
              "BU",
              "BX",
              "CA",
              "CD",
              "CG",
              "CI",
              "CK",
              "CM",
              "CP",
              "CS",
              "CU",
              "CX",
              "DA",
              "DD",
              "DF",
              "DH",
              "DJ",
              "DL",
            ],
            keys: [
              "1.1",
              "1.2",
              "2.1",
              "3.1",
              "4.1",
              "5.1",
              "5.2",
              "5.3",
              "5.4",
              "6.1",
              "7.1",
              "7.2",
              "8.1",
              "9.1",
              "10.1",
              "10.2",
              "10.3",
              "10.4",
              "10.5",
            ],
            path: "capacidades_pecuario",
            columnasSalida: ["DN", "DO", "DP", "DQ", "DR", "DS", "DT"],
          });

          // Diferencia porcentual -PLANIFICACION PREDIAL/MANEJO DE LA UNIDAD PRODUCTIVA
          setDiferenciaPorcentual(
            worksheet,
            "BV",
            currentRow,
            ["BR", "BT"], // iniciales
            ["BS", "BU"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -. COSTOS DE PRODUCCION
          setDiferenciaPorcentual(
            worksheet,
            "BY",
            currentRow,
            ["BW"], // iniciales
            ["BX"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - INFRAESTRUCTURA PRODUCTIVA
          setDiferenciaPorcentual(
            worksheet,
            "CB",
            currentRow,
            ["BZ"], // iniciales
            ["CA"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - PARÁMETROS PRODUCTIVOS
          setDiferenciaPorcentual(
            worksheet,
            "CE",
            currentRow,
            ["CC"], // iniciales
            ["CD"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - ALIMENTACIÓN Y NUTRICIÓN
          setDiferenciaPorcentual(
            worksheet,
            "CN",
            currentRow,
            ["CF", "CH", "CJ", "CL"], // iniciales
            ["CG", "CI", "CK", "CM"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - MANEJO REPRODUCTIVO
          setDiferenciaPorcentual(
            worksheet,
            "CQ",
            currentRow,
            ["CO"], // iniciales
            ["CP"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -  SANIDAD Y BIOSEGURIDAD ANIMAL
          setDiferenciaPorcentual(
            worksheet,
            "CV",
            currentRow,
            ["CR", "CT"], // iniciales
            ["CS", "CU"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - BUENAS PRACTICAS PARA SISTEMA PRODUCTIVO
          setDiferenciaPorcentual(
            worksheet,
            "CY",
            currentRow,
            ["CW"], // iniciales
            ["CX"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - BUENAS PRACTICAS DE ORDEÑO
          setDiferenciaPorcentual(
            worksheet,
            "DB",
            currentRow,
            ["CZ"], // iniciales
            ["DA"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual - PROTECCION DE ECOSISTEMAS, SALUD DEL SUELO Y MANEJO DE RESIDUOS Y CUIDADO DEL AGUA
          setDiferenciaPorcentual(
            worksheet,
            "DM",
            currentRow,
            ["DC", "DE", "DG", "DI", "DK"], // iniciales
            ["DD", "DF", "DH", "DJ", "DL"], // finales
            cambio_linea_prod
          );
          const detalle_ultima_novedad = item?.detalle_ultima_novedad || {};

          const mensajeNovedad = `Se realiza cambio de línea productiva: de Pecuaria a ${
            detalle_ultima_novedad.name_sector_economico_cambio ?? ""
          }.`;

          worksheet.getCell(`DU${currentRow}`).value =
            cambio_linea_prod === "SI" ? mensajeNovedad : "NO";

          const bloques = [socio, enfoque, capacidades_pecuario];

          const totalGeneral = bloques.reduce(
            (acc, b) => acc + safeNumber(b.totalCalificacion),
            0
          );

          const sumaM1General = bloques.reduce(
            (acc, b) => acc + safeNumber(b.sumaM1),
            0
          );

          const sumaM2General = bloques.reduce(
            (acc, b) => acc + safeNumber(b.sumaM2),
            0
          );

          const porcentajeM1General =
            totalGeneral > 0
              ? (sumaM1General / totalGeneral) * 100
              : "No aplica";

          const porcentajeM2General =
            totalGeneral > 0
              ? (sumaM2General / totalGeneral) * 100
              : "No aplica";

          const puedeComparar = bloques.some(
            (b) => b.diferencia !== "No aplica"
          );

          const diferenciaGeneral = puedeComparar
            ? sumaM2General - sumaM1General
            : "No aplica";

          const diferenciaPorcentualGeneral = puedeComparar
            ? porcentajeM2General - porcentajeM1General
            : "No aplica";

          worksheet.getCell(`DV${currentRow}`).value = totalGeneral;
          worksheet.getCell(`DW${currentRow}`).value = sumaM1General;

          worksheet.getCell(`DX${currentRow}`).value =
            porcentajeM1General === "No aplica"
              ? "No aplica"
              : `${porcentajeM1General.toFixed(2)}%`;

          worksheet.getCell(`DY${currentRow}`).value = sumaM2General;

          worksheet.getCell(`DZ${currentRow}`).value =
            porcentajeM2General === "No aplica"
              ? "No aplica"
              : `${porcentajeM2General.toFixed(2)}%`;

          worksheet.getCell(`EA${currentRow}`).value = diferenciaGeneral;

          worksheet.getCell(`EB${currentRow}`).value =
            diferenciaPorcentualGeneral === "No aplica"
              ? "No aplica"
              : `${diferenciaPorcentualGeneral.toFixed(2)}%`;

          worksheet.getCell(`EC${currentRow}`).value = item.departamento || "";
          worksheet.getCell(`ED${currentRow}`).value = item.municipio || "";
          currentRow++;
        }
      };

      const procesarHojaServicios = async (worksheet, data) => {
        if (!Array.isArray(data) || data.length === 0) return;

        let currentRow = 5;

        for (const item of data) {
          const detalle = item.detalle_tipo_3 || item.detalle_tipo_8 || null;
          const estado = item.estado || "";
          const cambio_linea_prod = item?.cambio_linea_prod || "";

          if (detalle) {
            llenarColumnasComunes(worksheet, detalle, currentRow, estado);
          } else {
            console.warn(
              `⚠️ Sin detalle para participante ${item.id_participante}`
            );
          }
          const detalle3 = item.detalle_tipo_3 || null;
          const detalle8 = item.detalle_tipo_8 || null;

          // --- 🟢 Bloque SOCIOEMPRESARIAL
          const socio = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: [
              "AB",
              "AE",
              "AG",
              "AI",
              "AL",
              "AN",
              "AP",
              "AR",
              "AT",
              "AW",
            ],
            colsM2: [
              "AC",
              "AF",
              "AH",
              "AJ",
              "AM",
              "AO",
              "AQ",
              "AS",
              "AU",
              "AX",
            ],
            keys: [
              "1.1",
              "2.1",
              "2.2",
              "2.3",
              "3.1",
              "3.2",
              "3.3",
              "3.4",
              "3.5",
              "4.1",
            ],
            path: "capacidades_socio_empresarial",
            columnasSalida: ["AZ", "BA", "BB", "BC", "BD", "BE", "BF"], // Donde escribir resultados
          });

          setDiferenciaPorcentual(
            worksheet,
            "AD",
            currentRow,
            ["AB"], // iniciales
            ["AC"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -FINANCIERA
          setDiferenciaPorcentual(
            worksheet,
            "AK",
            currentRow,
            ["AE", "AG", "AI"], // iniciales
            ["AF", "AH", "AJ"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -COMERCIAL
          setDiferenciaPorcentual(
            worksheet,
            "AV",
            currentRow,
            ["AL", "AN", "AP", "AR", "AT"], // iniciales (5)
            ["AM", "AO", "AQ", "AS", "AU"], // finales (5)
            cambio_linea_prod
          );

          // Diferencia porcentual -COMERCIAL
          setDiferenciaPorcentual(
            worksheet,
            "AV",
            currentRow,
            ["AL", "AN", "AP", "AR", "AT"], // iniciales (5)
            ["AM", "AO", "AQ", "AS", "AU"], // finales (5)
            cambio_linea_prod
          );

          // Diferencia porcentual -TICS
          setDiferenciaPorcentual(
            worksheet,
            "AY",
            currentRow,
            ["AW"], // iniciales (5)
            ["AX"], // finales (5)
            cambio_linea_prod
          );

          // --- 🟢 Bloque ENFOQUE TRANSVERSAL
          const enfoque = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: ["BG", "BI"],
            colsM2: ["BH", "BJ"],
            keys: ["1.1", "1.2"],
            path: "capacidades_enfoque_transversal",
            columnasSalida: ["BK", "BL", "BM", "BN", "BO", "BP", "BQ"],
          });

          // --- 🟢 CAPACIDADES DE SERVICIOS
          const capacidades_servicios = procesarBloque({
            worksheet,
            row: currentRow,
            detalle3,
            detalle8,
            colsM1: ["BR", "BT", "BW", "BZ", "CC", "CF", "CH", "CK", "CN"],
            colsM2: ["BS", "BU", "BX", "CA", "CD", "CG", "CI", "CL", "CO"],
            keys: [
              "1.1",
              "1.2",
              "2.1",
              "3.1",
              "34.1",
              "5.1",
              "5.2",
              "6.1",
              "7.1",
            ],
            path: "capacidades_servicios",
            columnasSalida: ["CQ", "CR", "CS", "CT", "CU", "CV", "CW"],
          });

          // Diferencia porcentual -PLANIFICACION Y GESTION DEL TIEMPO
          setDiferenciaPorcentual(
            worksheet,
            "BV",
            currentRow,
            ["BR", "BT"], // iniciales
            ["BS", "BU"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -COSTOS DE SERVICIOS
          setDiferenciaPorcentual(
            worksheet,
            "BY",
            currentRow,
            ["BW"], // iniciales
            ["BX"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -MANTENIMIENTO
          setDiferenciaPorcentual(
            worksheet,
            "CB",
            currentRow,
            ["BZ"], // iniciales
            ["CA"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -SEGUIRDAD
          setDiferenciaPorcentual(
            worksheet,
            "CE",
            currentRow,
            ["CC"], // iniciales
            ["CD"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -PROTECCION DE ECOSISTEMAS, SALUD DEL SUELO Y MANEJO DE RESIDUOS
          setDiferenciaPorcentual(
            worksheet,
            "CJ",
            currentRow,
            ["CF", "CH"], // iniciales
            ["CG", "CI"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -PRESTACION EL SERVICIO
          setDiferenciaPorcentual(
            worksheet,
            "CM",
            currentRow,
            ["CK"], // iniciales
            ["CL"], // finales
            cambio_linea_prod
          );

          // Diferencia porcentual -SALUD
          setDiferenciaPorcentual(
            worksheet,
            "CP",
            currentRow,
            ["CN"], // iniciales
            ["CO"], // finales
            cambio_linea_prod
          );
          const detalle_ultima_novedad = item?.detalle_ultima_novedad || {};

          const mensajeNovedad = `Se realiza cambio de línea productiva: de Servicios a ${
            detalle_ultima_novedad.name_sector_economico_cambio ?? ""
          }.`;

          worksheet.getCell(`CX${currentRow}`).value =
            cambio_linea_prod === "SI" ? mensajeNovedad : "NO";

          const bloques = [socio, enfoque, capacidades_servicios];

          const totalGeneral = bloques.reduce(
            (acc, b) => acc + safeNumber(b.totalCalificacion),
            0
          );

          const sumaM1General = bloques.reduce(
            (acc, b) => acc + safeNumber(b.sumaM1),
            0
          );

          const sumaM2General = bloques.reduce(
            (acc, b) => acc + safeNumber(b.sumaM2),
            0
          );

          const porcentajeM1General =
            totalGeneral > 0
              ? (sumaM1General / totalGeneral) * 100
              : "No aplica";

          const porcentajeM2General =
            totalGeneral > 0
              ? (sumaM2General / totalGeneral) * 100
              : "No aplica";

          const puedeComparar = bloques.some(
            (b) => b.diferencia !== "No aplica"
          );

          const diferenciaGeneral = puedeComparar
            ? sumaM2General - sumaM1General
            : "No aplica";

          const diferenciaPorcentualGeneral = puedeComparar
            ? porcentajeM2General - porcentajeM1General
            : "No aplica";
          worksheet.getCell(`CY${currentRow}`).value = totalGeneral;
          worksheet.getCell(`CZ${currentRow}`).value = sumaM1General;

          worksheet.getCell(`DA${currentRow}`).value =
            porcentajeM1General === "No aplica"
              ? "No aplica"
              : `${porcentajeM1General.toFixed(2)}%`;

          worksheet.getCell(`DB${currentRow}`).value = sumaM2General;

          worksheet.getCell(`DC${currentRow}`).value =
            porcentajeM2General === "No aplica"
              ? "No aplica"
              : `${porcentajeM2General.toFixed(2)}%`;

          worksheet.getCell(`DD${currentRow}`).value = diferenciaGeneral;

          worksheet.getCell(`DE${currentRow}`).value =
            diferenciaPorcentualGeneral === "No aplica"
              ? "No aplica"
              : `${diferenciaPorcentualGeneral.toFixed(2)}%`;
          worksheet.getCell(`DF${currentRow}`).value = item.departamento || "";
          worksheet.getCell(`DG${currentRow}`).value = item.municipio || "";
          currentRow++;
        }
      };

      const datosAgrupados = groupByLineaProductiva(dataCompleta);
      const datasets = [
        {
          name: "Agrícola",
          data: datosAgrupados.Agrícola,
          processor: procesarHojaAgricola,
        },

        {
          name: "Pecuaria",
          data: datosAgrupados.Pecuaria,
          processor: procesarHojaPecuaria,
        },

        {
          name: "Servicios",
          data: datosAgrupados.Servicios,
          processor: procesarHojaServicios,
        },
      ];

      await Promise.all(
        datasets.map(async ({ name, data, processor }) => {
          let worksheet = workbook.getWorksheet(name);
          if (!worksheet) {
            worksheet = workbook.addWorksheet(name);
          }
          await processor(worksheet, data); // ✅ Ahora espera a que termine cada procesamiento
        })
      );

      // Descargar el archivo modificado
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      saveAs(blob, "Informe_comparativo.xlsx");
      notification.success({
        message: "Exito",
        description: "Excel exportado correctamente.",
        duration: 3,
      });
    } catch (error) {
      console.error("Error al generar Excel:", error);
      notification.error({
        message: "Error",
        description: "No se pudo exportar el Excel.",
        duration: 3,
      });
    } finally {
      setLoadingExcel(false);
    }
  };

  const exportToJSON = async () => {
    try {
      if (!dataCompleta || dataCompleta.length === 0) {
        notification.warning({
          message: "Advertencia",
          description: "No hay datos disponibles para exportar.",
          duration: 3,
        });
        return;
      }
      setLoadingJson(true);

      // Convertir data a JSON y descargar
      const jsonBlob = new Blob([JSON.stringify(dataCompleta, null, 2)], {
        type: "application/json",
      });
      saveAs(jsonBlob, "Informe_medicion_capacidades_salida.json");

      notification.success({
        message: "Éxito",
        description: "JSON exportado correctamente.",
        duration: 3,
      });
    } catch (error) {
      console.error("Error al generar JSON:", error);
      notification.error({
        message: "Error",
        description: "No se pudo exportar el JSON.",
        duration: 3,
      });
    } finally {
      setLoadingJson(false);
    }
  };

  return (
    <Space>
      <Tooltip title="Exportar datos en XLSX">
        <Button
          type="default"
          icon={<FileExcelOutlined />}
          onClick={exportToExcel}
          loading={loading || loadingExcel}
          disabled={loading || loadingExcel}
          style={{
            backgroundColor: "#52c41a",
            borderColor: "#52c41a",
            color: "white",
          }}
        />
      </Tooltip>

      <Tooltip title="Exportar datos en JSON">
        <Button
          type="default"
          icon={<FileTextOutlined />}
          onClick={exportToJSON}
          loading={loading || loadingJson}
          disabled={loading || loadingJson}
          style={{
            backgroundColor: "#C2BCBC",
            borderColor: "#C2BCBC",
            color: "white",
          }}
        />
      </Tooltip>
    </Space>
  );
};

export default Export_ComparativoAnalisisMedicion;
